import { CARTO_STYLE, googleAuthFailure, loadMapLibre, waitForGoogleMaps } from './basemap';

declare const google: any;

/** The closest Google zoom fitTo goes to (17: a few buildings either side, street names showing). */
const FIT_MAX_ZOOM = 17;

/**
 * A plain pin map (#248 review, #250 venue map; plan §5.1: "plain Google Maps markers, no deck.gl; works on tablets").
 * Google Maps where Conductor's key is allowed, else MapLibre + CARTO (the same fallback as the suburb map).
 *
 * Pins carry crawled names, which are untrusted text (plan §3.3): labels and titles are set as text, never HTML.
 */

export interface MapPin {
  id: string;
  lat: number;
  lng: number;
  /** A short label drawn on the pin (1, 2, 3, or a letter). */
  label?: string;
  /** Shown on hover. */
  title: string;
  colour: string;
  /** A smaller pin (lots of them: the crawl's places). */
  small?: boolean;
}

export class SimpleMap {
  kind: 'google' | 'maplibre' | null = null;
  private gmap: any;
  private mlmap: any;
  private maplibregl: any;
  private markers: any[] = [];
  private dropMarker: any = null;
  private clickHandlers: Array<(lat: number, lng: number) => void> = [];
  private moveHandlers: Array<() => void> = [];

  constructor(private el: HTMLElement) {}

  async init(center: { lat: number; lng: number }, zoom = 13): Promise<void> {
    let googleOk = false;
    try {
      await waitForGoogleMaps(8000);
      googleOk = await this.initGoogle(center, zoom);
    } catch {
      googleOk = false;
    }
    if (!googleOk) {
      this.el.innerHTML = ''; // Google's "something went wrong" panel
      await this.initMapLibre(center, zoom);
    }
  }

  private async initGoogle(center: { lat: number; lng: number }, zoom: number): Promise<boolean> {
    const failed = googleAuthFailure().then(() => 'failed' as const);
    if ((window as any).__gmAuthFailed) return false;
    const gmap = new google.maps.Map(this.el, {
      center, zoom, mapTypeId: 'roadmap', disableDefaultUI: true, zoomControl: true, scaleControl: true, gestureHandling: 'greedy', clickableIcons: false,
      styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
    });
    const tiles = new Promise<'ok'>((resolve) => google.maps.event.addListenerOnce(gmap, 'tilesloaded', () => resolve('ok')));
    if ((await Promise.race([failed, tiles, new Promise<'ok'>((r) => setTimeout(() => r('ok'), 5000))])) === 'failed') {
      google.maps.event.clearInstanceListeners(gmap);
      return false;
    }
    gmap.addListener('click', (e: any) => this.clickHandlers.forEach((h) => h(e.latLng.lat(), e.latLng.lng())));
    gmap.addListener('dragend', () => this.moveHandlers.forEach((h) => h()));
    this.gmap = gmap;
    this.kind = 'google';
    return true;
  }

  private async initMapLibre(center: { lat: number; lng: number }, zoom: number): Promise<void> {
    const maplibregl = await loadMapLibre();
    const m = new maplibregl.Map({ container: this.el, style: CARTO_STYLE, center: [center.lng, center.lat], zoom: zoom - 1, attributionControl: { compact: true }, dragRotate: false });
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    await new Promise((resolve) => m.on('load', resolve));
    m.on('click', (e: any) => this.clickHandlers.forEach((h) => h(e.lngLat.lat, e.lngLat.lng)));
    m.on('dragend', () => this.moveHandlers.forEach((h) => h()));
    this.maplibregl = maplibregl;
    this.mlmap = m;
    this.kind = 'maplibre';
  }

  /** Called with the point clicked on the map (for dropping a pin). */
  onClick(handler: (lat: number, lng: number) => void): void {
    this.clickHandlers.push(handler);
  }

  /** Called when someone drags the map somewhere else (not when the code moves it). */
  onMoved(handler: () => void): void {
    this.moveHandlers.push(handler);
  }

  /** Where the map is centred now. */
  center(): { lat: number; lng: number } | null {
    if (this.kind === 'google' && this.gmap) {
      const c = this.gmap.getCenter();
      return { lat: c.lat(), lng: c.lng() };
    }
    if (this.mlmap) {
      const c = this.mlmap.getCenter();
      return { lat: c.lat, lng: c.lng };
    }
    return null;
  }

  private pinElement(pin: Pick<MapPin, 'label' | 'title' | 'colour' | 'small'>, dashed = false): HTMLElement {
    const d = document.createElement('div');
    d.textContent = pin.label ?? '';
    d.title = pin.title;
    Object.assign(d.style, {
      width: pin.small ? '16px' : '26px', height: pin.small ? '16px' : '26px', borderRadius: '50% 50% 50% 0', transform: 'rotate(-45deg)', background: pin.colour,
      border: dashed ? '2px dashed #0f172a' : '2px solid #ffffff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)', color: '#ffffff',
      font: '600 12px system-ui, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    });
    // Keep the label upright inside the rotated pin.
    const inner = document.createElement('span');
    inner.textContent = pin.label ?? '';
    inner.style.transform = 'rotate(45deg)';
    d.textContent = '';
    d.appendChild(inner);
    return d;
  }

  /** Replace the pins. `onPick` gets a pin's id when it's clicked. */
  setPins(pins: MapPin[], onPick?: (id: string) => void): void {
    for (const m of this.markers) (this.kind === 'google' ? m.setMap(null) : m.remove());
    this.markers = [];
    for (const p of pins) {
      if (this.kind === 'google') {
        const marker = new google.maps.Marker({
          map: this.gmap, position: { lat: p.lat, lng: p.lng }, title: p.title,
          label: p.label ? { text: p.label, color: '#ffffff', fontWeight: '600' } : undefined,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: p.small ? 6 : 12, fillColor: p.colour, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: p.small ? 1 : 2 },
        });
        if (onPick) marker.addListener('click', () => onPick(p.id));
        this.markers.push(marker);
      } else if (this.kind === 'maplibre') {
        const el = this.pinElement(p);
        if (onPick) el.addEventListener('click', (e) => { e.stopPropagation(); onPick(p.id); });
        this.markers.push(new this.maplibregl.Marker({ element: el, anchor: 'bottom-left' }).setLngLat([p.lng, p.lat]).addTo(this.mlmap));
      }
    }
  }

  /** The pin being dropped by hand (null to remove it). */
  setDropPin(pos: { lat: number; lng: number } | null): void {
    if (this.dropMarker) (this.kind === 'google' ? this.dropMarker.setMap(null) : this.dropMarker.remove());
    this.dropMarker = null;
    if (!pos) return;
    if (this.kind === 'google') {
      this.dropMarker = new google.maps.Marker({ map: this.gmap, position: pos, title: 'New pin' });
    } else if (this.kind === 'maplibre') {
      this.dropMarker = new this.maplibregl.Marker({ element: this.pinElement({ label: '★', title: 'New pin', colour: '#f59e0b' }, true), anchor: 'bottom-left' })
        .setLngLat([pos.lng, pos.lat])
        .addTo(this.mlmap);
    }
  }

  /** Centre on a point, keeping the zoom (or at this zoom). */
  panTo(p: { lat: number; lng: number }, zoom?: number): void {
    if (this.kind === 'google' && this.gmap) {
      this.gmap.panTo(p);
      if (zoom) this.gmap.setZoom(zoom);
    } else this.mlmap?.easeTo({ center: [p.lng, p.lat], ...(zoom ? { zoom: zoom - 1 } : {}), duration: 300 });
  }

  /** Fit the view to these points (one point: centre on it at street zoom). */
  fitTo(points: Array<{ lat: number; lng: number }>): void {
    if (!points.length) return;
    if (points.length === 1) {
      if (this.kind === 'google') { this.gmap.setCenter(points[0]); this.gmap.setZoom(15); }
      else this.mlmap?.jumpTo({ center: [points[0].lng, points[0].lat], zoom: 14 });
      return;
    }
    const w = Math.min(...points.map((p) => p.lng)), e = Math.max(...points.map((p) => p.lng));
    const s = Math.min(...points.map((p) => p.lat)), n = Math.max(...points.map((p) => p.lat));
    if (this.kind === 'google') {
      // Points metres apart would zoom to the last level, with nothing around them to recognise: stop at FIT_MAX_ZOOM.
      google.maps.event.addListenerOnce(this.gmap, 'bounds_changed', () => {
        if (this.gmap.getZoom() > FIT_MAX_ZOOM) this.gmap.setZoom(FIT_MAX_ZOOM);
      });
      this.gmap.fitBounds(new google.maps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e }), 40);
    } else this.mlmap?.fitBounds([[w, s], [e, n]], { padding: 50, duration: 0, maxZoom: 15 });
  }

  resize(): void {
    if (this.kind === 'google') google.maps.event.trigger(this.gmap, 'resize');
    else this.mlmap?.resize();
  }

  destroy(): void {
    this.setPins([]);
    this.setDropPin(null);
    if (this.kind === 'google' && this.gmap) google.maps.event.clearInstanceListeners(this.gmap);
    this.mlmap?.remove();
    this.gmap = this.mlmap = null;
  }
}
